import { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';

type Severity = 'success' | 'info' | 'warning' | 'error';

type ShowFn = (message: string, severity?: Severity) => void;

type SnackbarContextValue = {
  show: ShowFn;
};

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

type SnackbarState = {
  open: boolean;
  message: string;
  severity: Severity;
};

const initialState: SnackbarState = {
  open: false,
  message: '',
  severity: 'info',
};

type SnackbarProviderProps = {
  readonly children: ReactNode;
};

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [state, setState] = useState<SnackbarState>(initialState);

  const show = useCallback<ShowFn>((message, severity = 'info') => {
    setState({ open: true, message, severity });
  }, []);

  const handleClose = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <SnackbarContext.Provider value={{ show }}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={state.severity} variant="filled" onClose={handleClose}>
          {state.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): SnackbarContextValue {
  const ctx = useContext(SnackbarContext);
  if (!ctx) {
    throw new Error('useSnackbar must be used within SnackbarProvider');
  }
  return ctx;
}
